import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import GpayIframeButton from '../../components/gpay-iframe-button/GpayIframeButton';
import { BASE_API_URL, BASE_PUBLIC_API_URL } from '../../constants/api-path.c';
import Api from '../../utils/Api';
import './style.css';

// Event types for postMessage
const PAYMENT_EVENTS = {
  PAYMENT_STARTED: 'PAYMENT_STARTED',
  PAYMENT_SUCCESS: 'PAYMENT_SUCCESS',
  PAYMENT_ERROR: 'PAYMENT_ERROR',
  PAYMENT_CANCELLED: 'PAYMENT_CANCELLED',
  PAYMENT_AUTHORIZED: 'PAYMENT_AUTHORIZED'
};

const GpayIframePage = () => {
  const [searchParams] = useSearchParams();
  const [apiData, setApiData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const token = searchParams.get('token');
  const iFrameToken = searchParams.get('iframeToken');
  const sessionNonce = searchParams.get('nonce') || crypto.randomUUID();
  const [isFrameTokenValidated, serIsFrameTokenValidated] = useState<boolean>(false);


  // Function to send messages to parent window
  const notifyParent = (eventType: string, data?: any) => {
    const message = {
      type: eventType,
      nonce: sessionNonce,
      timestamp: Date.now(),
      paymentData: data
    };

    window.parent.postMessage(message, '*'); // You can restrict the target origin for security
  };

  useEffect(() => {
    // Remove any existing background styles
    document.body.style.background = 'transparent';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'hidden';

    if (token) {
      try {
        const decodedToken = atob(token);
        const paymentData = JSON.parse(decodedToken);

        if (!paymentData.pspConfig) {
          setError('Missing payment configuration');
          notifyParent(PAYMENT_EVENTS.PAYMENT_ERROR, {
            message: 'Missing payment configuration',
            error: 'CONFIG_ERROR'
          });
          return;
        }

        setApiData({
          ...paymentData
        });
      } catch (error) {
        console.error('Error processing token:', error);
        const errorMessage = error instanceof SyntaxError ? 'Invalid token format' : 'Error decoding token';
        setError(errorMessage);
        notifyParent(PAYMENT_EVENTS.PAYMENT_ERROR, {
          message: errorMessage,
          error: 'TOKEN_ERROR'
        });
      }
    } else {
      setError('No token provided');
      notifyParent(PAYMENT_EVENTS.PAYMENT_ERROR, {
        message: 'No token provided',
        error: 'TOKEN_MISSING'
      });
    }
  }, [token]);

  // Validate iframe token after we have the apiData
  useEffect(() => {
    const validateIframeToken = async () => {
      if (!iFrameToken) {
        setError('No iFrame token provided');
        notifyParent(PAYMENT_EVENTS.PAYMENT_ERROR, {
          message: 'No Iframe token provided',
          error: 'IFRAME_TOKEN_MISSING'
        });
        return false;
      }

      if (!apiData || !apiData.pspConfig) {
        console.log('Waiting for apiData to be ready...');
        return false;
      }

      try {
        const response = await Api().post(
          `${BASE_PUBLIC_API_URL}/merchant/configuration/googlepay/validate-iframe-token`,
          {
            iFrameToken: iFrameToken,
            ...apiData
          }
        );
        serIsFrameTokenValidated(response?.success)
        return response?.success;
      } catch (error) {
        console.error("Token validation error:", error);
        return false;
      }
    };

    const validateToken = async () => {
      const isValidToken = await validateIframeToken();
      console.log("isValidToken", isValidToken);
      if (!isValidToken) {
        setError('Invalid iFrame token');
        notifyParent(PAYMENT_EVENTS.PAYMENT_ERROR, {
          message: 'Invalid Iframe token',
          error: 'IFRAME_TOKEN_INVALID'
        });
      }
    };

    if (apiData && apiData.pspConfig) {
      validateToken();
    }
  }, [apiData]);

  const handlePaymentSuccess = async (paymentData: any) => {
    try {
      notifyParent(PAYMENT_EVENTS.PAYMENT_STARTED, {
        status: 'processing',
        timestamp: Date.now()
      });

      const response = await Api().post(`${BASE_API_URL}/wallet/capture/payment/${apiData.mid}`, {
        URLKey: apiData.slug,
        amount: Number(apiData.transactionInfo.totalPrice),
        currency: apiData.transactionInfo.currencyCode,
        billing_address: apiData.transactionInfo.billing_address,
        return_url: apiData.transactionInfo.return_url,
        payment_method: {
          network: paymentData.paymentMethodData.info?.cardNetwork,
          type: "googlepay",
          googlePayToken: JSON.stringify(paymentData.paymentMethodData.tokenizationData.token),
        },
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone
      });

   
      // Send payment authorized event with complete response data
      if (![200, 201].includes(response?.status_code)) {
        console.log("response", response?.status_code);
        notifyParent(PAYMENT_EVENTS.PAYMENT_ERROR, {
          ...response,
          message: 'Payment processing failed',
          type: 'PAYMENT_ERROR',
          timestamp: Date.now()
        });
        return;
      }

      notifyParent(PAYMENT_EVENTS.PAYMENT_AUTHORIZED, {
        ...response,
        paymentMethod: {
          type: 'googlepay',
          network: paymentData.paymentMethodData.info?.cardNetwork
        },
        transactionInfo: {
          amount: apiData.transactionInfo.totalPrice,
          currency: apiData.transactionInfo.currencyCode
        }
      });
    } catch (error) {
      console.log("error", error);
      notifyParent(PAYMENT_EVENTS.PAYMENT_ERROR, {
        message: error instanceof Error ? error.message : 'Payment processing failed',
        error: 'PAYMENT_ERROR',
        timestamp: Date.now()
      });
    }
  };

  if (error) {
    console.error('Error:', error);
    return null;
  }

  if (!apiData || !apiData.pspConfig) {
    return null;
  }

  return isFrameTokenValidated ? <GpayIframeButton apiData={apiData} onPaymentSuccess={handlePaymentSuccess} /> : <></>;
};

export default GpayIframePage; 